/**
 * 표 뷰 — 노션의 데이터베이스 표.
 *
 * **보드가 못 하는 것을 한다**: 여러 칸을 한 번에 훑고, 열로 정렬한다.
 * 그래서 여기에는 칸 열이 있고 카드에는 없다(카드는 이미 그 칸 안에 있다).
 *
 * 정렬은 **화면에서** 한다. 서버 정렬을 붙이면 자리(`position`)와 정렬이
 * 두 축이 되어 「표에서 정렬하고 보드로 갔더니 순서가 다르다」가 된다.
 */

import { useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { TicketCard } from '../api';
import { columnLabel, PRIORITIES, workSpan } from '../columns';
import { AssigneeMark, BADGE, DueMark, LabelChip, PriorityMark, TypeMark } from './Chips';

type SortKey = 'key' | 'title' | 'col' | 'priority' | 'assignee' | 'due' | 'work';

/** 우선순위는 이름순이 아니라 **급한 순**이다. */
const PRIORITY_RANK = new Map(PRIORITIES.map((item, at) => [item.id, at]));

function compare(a: TicketCard, b: TicketCard, key: SortKey): number {
  switch (key) {
    case 'key':
      return a.id - b.id;
    case 'title':
      return a.title.localeCompare(b.title, 'ko');
    case 'col':
      return a.col.localeCompare(b.col);
    case 'priority': {
      // 안 정한 것은 **맨 뒤**다. 없는 값을 「가장 낮음」으로 섞으면
      // 「낮음」과 구분이 사라진다.
      const ra = a.priority ? (PRIORITY_RANK.get(a.priority) ?? 9) : 9;
      const rb = b.priority ? (PRIORITY_RANK.get(b.priority) ?? 9) : 9;
      return ra - rb;
    }
    case 'assignee':
      return (a.assignee?.display_name ?? '￿').localeCompare(
        b.assignee?.display_name ?? '￿',
        'ko',
      );
    case 'due':
      return (a.due_date ?? '￿').localeCompare(b.due_date ?? '￿');
    case 'work':
      // 안 시작한 것은 맨 뒤. 「언제부터 붙잡고 있나」를 훑는 열이다.
      return (a.started_at ?? '￿').localeCompare(b.started_at ?? '￿');
  }
}

export function TicketTableView({
  tickets,
  onOpen,
}: {
  tickets: TicketCard[];
  onOpen: (id: number) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: 'key',
    asc: true,
  });

  const rows = [...tickets].sort((a, b) => {
    const out = compare(a, b, sort.key);
    return sort.asc ? out : -out;
  });

  function head(key: SortKey, label: string, extra?: string) {
    const on = sort.key === key;
    return (
      <TableHead scope="col" className={extra}>
        <button
          type="button"
          className="inline-flex items-center gap-[3px]"
          aria-sort={on ? (sort.asc ? 'ascending' : 'descending') : 'none'}
          onClick={() =>
            setSort((prev) => ({ key, asc: prev.key === key ? !prev.asc : true }))
          }
        >
          {label}
          <span aria-hidden="true" className="text-micro text-ink-3">
            {on ? (sort.asc ? '▲' : '▼') : ''}
          </span>
        </button>
      </TableHead>
    );
  }

  if (rows.length === 0) {
    return <p className="page-status">티켓 없음</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-panel shadow-panel">
      <Table>
        <TableHeader>
          <TableRow>
            {head('key', '번호', 'w-[92px]')}
            {head('title', '제목')}
            {head('col', '상태', 'w-[92px]')}
            {head('priority', '우선순위', 'w-[92px]')}
            {head('assignee', '담당', 'w-[110px]')}
            {head('due', '마감', 'w-[76px]')}
            {head('work', '작업', 'w-[150px]')}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((ticket) => (
            <TableRow
              key={ticket.id}
              className="cursor-pointer"
              onClick={() => onOpen(ticket.id)}
            >
              <TableCell className="tnum text-ink-3">{ticket.key}</TableCell>
              <TableCell>
                <span className="flex items-center gap-s2">
                  {/* 유형은 카드와 같은 규칙 — 이슈만 붉다. */}
                  {ticket.kind === 'issue' && (
                    <span
                      className={`${BADGE} bg-st-red-t font-bold text-st-red-f`}
                      title="이슈"
                    >
                      이슈
                    </span>
                  )}
                  <TypeMark type={ticket.type} />
                  <span className="break-keep [overflow-wrap:anywhere]">
                    {ticket.title}
                  </span>
                  {ticket.labels.map((label) => (
                    <LabelChip key={label.id} label={label} />
                  ))}
                </span>
              </TableCell>
              <TableCell className="text-ink-2">{columnLabel(ticket.col)}</TableCell>
              <TableCell>
                <PriorityMark priority={ticket.priority} />
              </TableCell>
              <TableCell>
                {ticket.assignee ? (
                  <span className="flex items-center gap-s2">
                    <AssigneeMark user={ticket.assignee} size={18} />
                    <span className="truncate">{ticket.assignee.display_name}</span>
                  </span>
                ) : null}
              </TableCell>
              <TableCell>
                <DueMark due={ticket.due_date} />
              </TableCell>
              {/* 작업 기간과 완료. **표에만 있다** — 카드에 넣으면 줄이 하나
                  늘고, 보드에서 알고 싶은 것은 「어디까지 갔나」이지 날짜가
                  아니다. */}
              <TableCell className="tnum text-ink-2">
                {ticket.finished_at ? (
                  <span className="text-st-green-f">
                    완료 {ticket.finished_at.slice(5, 10).replace('-', '/')}
                  </span>
                ) : (
                  (workSpan(ticket.started_at, ticket.estimate_days) ?? '')
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
